import { Button, Spinner } from "@heroui/react";
import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api, S3_URL } from "@/api";
import { toast, ToastContainer } from "react-toastify";
import useErrorHandling from "@/hooks/useErrorHandling";
import { Stepper } from "@/app/components/initiate/Stepper";
import { TempAgreement } from "@/api/temp-agreements/temp-agreements";
import { CompanyForm } from "@/app/components/initiate/CompanyForm";
import { BankDetailsForm } from "@/app/components/initiate/BankDetailsForm";
import { AgreementDetailsForm } from "@/app/components/initiate/AgreementDetailsForm";

export function InitiatePage() {
  const { hash } = useParams() as { hash: string };
  const handleError = useErrorHandling();
  const navigate = useNavigate();

  const isAuthorized = localStorage.getItem("access_token") !== null;
  const [activeStep, setActiveStep] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [tempAgreement, setTempAgreement] = useState<TempAgreement | null>(
    null,
  );

  const [renterId, setRenterId] = useState<number | undefined>(undefined);
  const [requisiteId, setRequisiteId] = useState<number | undefined>(undefined);

  const steps = [
    {
      name: "Авторизация",
      description: "Необходима для привязки компании к вашему профилю",
      longDescription:
        "На данном этапе вам необходимо авторизоваться в системе.",
    },
    {
      name: "Данные о компании",
      description: "Необходимо для создания компании",
      longDescription:
        "На данном этапе вам необходимо ввести данные о компании.",
    },
    {
      name: "Реквизиты",
      description: "Необходимо для создания договора аренды",
      longDescription: "На данном этапе вам необходимо ввести реквизиты.",
    },
    {
      name: "Договор аренды",
      description: "Ознакомьтесь с договором и подтвердите его",
      longDescription:
        "На данном этапе вам необходимо ознакомиться с договором и подтвердить его.",
    },
  ];

  const changeStep = useCallback(
    (step: number) => {
      if (step > activeStep || step == 0) {
        toast.info("Вы не можете перейти к этому шагу");
        return;
      }
      if (step == activeStep) return;

      setActiveStep(step);
      console.log("Current tempAgreement:", tempAgreement);
    },
    [activeStep, tempAgreement],
  );

  const handleCompanySubmit = useCallback(
    (data: Partial<TempAgreement>) => {
      if (tempAgreement) {
        const updatedAgreement = {
          ...tempAgreement,
          ...data,
        };
        setTempAgreement(updatedAgreement);
        setActiveStep(2);
        console.log("Updated tempAgreement:", updatedAgreement);
      }
    },
    [tempAgreement],
  );

  const handleBankDetailsSubmit = useCallback(
    (data: Partial<TempAgreement>) => {
      if (tempAgreement) {
        const updatedAgreement = {
          ...tempAgreement,
          ...data,
        };
        setTempAgreement(updatedAgreement);
        setActiveStep(3);
        console.log("Updated tempAgreement:", updatedAgreement);
      }
    },
    [tempAgreement],
  );

  const handleAgreementSubmit = useCallback(() => {
    if (tempAgreement) {
      console.log("Final tempAgreement:", tempAgreement);
      api.tempAgreements
        .submit(hash, {
          ...tempAgreement,
          renter_id: renterId,
          requisite_id: requisiteId,
        })
        .then(() => {
          toast.success("Договор успешно подтвержден");
          navigate(`/dashboard/`);
        })
        .catch((err) => {
          const { errorMessage } = handleError(err);
          toast.error(errorMessage);
        });
    }
  }, [tempAgreement]);

  useEffect(() => {
    if (isAuthorized) {
      setActiveStep(1);
    }
  }, [isAuthorized, hash]);

  useEffect(() => {
    if (hash) {
      api.tempAgreements
        .get(hash)
        .then((data) => {
          setTempAgreement(data.data);
          setIsLoading(false);
        })
        .catch((err) => {
          const { errorMessage } = handleError(err);
          toast.error(errorMessage);
        });
    }
  }, [hash]);

  return (
    <>
      {isLoading && (
        <div className="w-screen h-dvh min-h-dvh flex flex-col items-center justify-center">
          <Spinner />
        </div>
      )}
      {!isLoading && tempAgreement && (
        <>
          <ToastContainer
            position="top-right"
            autoClose={5000}
            hideProgressBar={false}
            newestOnTop={true}
            closeOnClick
            rtl={false}
            pauseOnFocusLoss
            draggable
            pauseOnHover
            theme="dark"
          />
          <div className="w-screen h-dvh min-h-dvh flex flex-col items-center">
            <div className="w-screen max-w-screen-2xl flex flex-col p-4 gap-4 items-center">
              <div className="flex flex-col lg:flex-row w-full p-8 border-2 border-foreground-200 dark:border-foreground-100 rounded-xl bg-background">
                <img
                  src={
                    `${S3_URL}/${tempAgreement.main_photo}` ||
                    "https://lightwidget.com/wp-content/uploads/localhost-file-not-found.jpg"
                  }
                  className="aspect-video object-cover w-full lg:max-w-xl rounded-xl shadow"
                />
                <div className="flex flex-col p-8">
                  <span className="text-xl font-bold mb-2">
                    {tempAgreement.office_name}
                  </span>
                  <span className="text-sm text-gray-500">
                    {tempAgreement.business_profile_name} предлагает Вам
                    заключить договор аренды офиса «{tempAgreement.office_name}
                    ».
                  </span>
                  <span className="text-sm text-gray-500">
                    {steps[activeStep].longDescription}
                  </span>
                </div>
              </div>
              <div className="mb-2 w-full p-8 border-2 border-foreground-200 dark:border-foreground-100 rounded-xl bg-background">
                <Stepper
                  steps={steps}
                  activeStep={activeStep}
                  changeStep={changeStep}
                />
              </div>
              <div className="w-full">
                {activeStep == 0 && (
                  <div className="w-full flex flex-col gap-4">
                    <span>Вам необходимо авторизоваться в системе.</span>
                    <span>
                      После авторизации вас автоматически перенесут на следующий
                      шаг.
                    </span>
                    <Button
                      color="success"
                      onClick={() => {
                        navigate(
                          `/auth/login?redirect_to=${window.location.pathname}`,
                        );
                      }}
                    >
                      Авторизоваться
                    </Button>
                  </div>
                )}
                {activeStep == 1 && (
                  <div className="w-full flex flex-col gap-4">
                    <CompanyForm
                      defaults={tempAgreement}
                      onSubmit={handleCompanySubmit}
                      onRenterSelect={setRenterId}
                    />
                  </div>
                )}
                {activeStep == 2 && (
                  <div className="w-full flex flex-col gap-4">
                    <BankDetailsForm
                      defaults={tempAgreement}
                      onSubmit={handleBankDetailsSubmit}
                      renterId={renterId}
                      onRequisiteSelect={setRequisiteId}
                    />
                  </div>
                )}
                {activeStep == 3 && (
                  <div className="w-full flex flex-col gap-4">
                    <AgreementDetailsForm
                      data={tempAgreement}
                      onSubmit={handleAgreementSubmit}
                    />
                  </div>
                )}
              </div>
            </div>
          </div>
        </>
      )}
    </>
  );
}
